import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

import { useOrganization } from '@/contexts/OrganizationContext';
import {
  getAnalyticsRouteDescriptor,
  resolveAnalyticsContext,
  type AnalyticsNavigationSource,
  type AnalyticsNavigationSurface,
  type AnalyticsRouteDescriptor,
} from '@/lib/analyticsContext';
import { consumeAnalyticsNavigation } from '@/lib/analyticsNavigation';
import { trackAmplitudeEvent } from '@/lib/amplitudeAnalytics';

type PreviousRoute = {
  path: string;
  descriptor: AnalyticsRouteDescriptor;
};

function getFallbackNavigationSource(
  navigationType: ReturnType<typeof useNavigationType>,
  previousRoute: PreviousRoute | null,
): AnalyticsNavigationSource {
  if (navigationType === 'POP') {
    return previousRoute ? 'browser_back_forward' : 'direct_url';
  }

  if (navigationType === 'REPLACE') {
    return 'redirect';
  }

  return previousRoute ? 'unknown' : 'direct_url';
}

function getFallbackNavigationSurface(routePattern: string): AnalyticsNavigationSurface {
  if (routePattern === '/auth') {
    return 'auth';
  }

  if (routePattern === '/onboarding') {
    return 'onboarding';
  }

  if (routePattern.startsWith('/navigator')) {
    return 'navigator';
  }

  if (routePattern.startsWith('/vendor')) {
    return 'vendor_portal';
  }

  if (routePattern === '/dashboard') {
    return 'dashboard';
  }

  if (routePattern.startsWith('/suppliers/:supplierId')) {
    return 'supplier_profile';
  }

  if (routePattern === '/customers') {
    return 'customer_profile';
  }

  return 'unknown';
}

export function AmplitudePageAnalyticsBridge() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const previousRouteRef = useRef<PreviousRoute | null>(null);
  const latestContextRef = useRef<Parameters<typeof resolveAnalyticsContext>[0] | null>(null);
  const {
    organization,
    membership,
    isMspOrg,
    isLoading,
    isMspContext,
    mspHomeOrg,
  } = useOrganization();
  const path = `${location.pathname}${location.search}${location.hash}`;

  useEffect(() => {
    latestContextRef.current = {
      organization,
      membership,
      isMspOrg,
      isMspContext,
      mspHomeOrg,
      route: getAnalyticsRouteDescriptor(location.pathname),
    };
  }, [isMspContext, isMspOrg, location.pathname, membership, mspHomeOrg, organization]);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    const descriptor = getAnalyticsRouteDescriptor(location.pathname);
    const previousRoute = previousRouteRef.current;
    if (previousRoute?.path === path) {
      return;
    }

    const explicitNavigation = consumeAnalyticsNavigation(path);
    const navigationSource = explicitNavigation?.source ?? getFallbackNavigationSource(navigationType, previousRoute);
    const navigationSurface = explicitNavigation?.surface ?? getFallbackNavigationSurface(descriptor.routePattern);
    const contextProperties = resolveAnalyticsContext({
      ...(latestContextRef.current ?? {
        organization: null,
        membership: null,
        isMspOrg: false,
        isMspContext: false,
        mspHomeOrg: null,
      }),
      route: descriptor,
    });

    if (explicitNavigation && previousRoute) {
      trackAmplitudeEvent('Navigation Performed', {
        'from page key': previousRoute.descriptor.pageKey,
        'to page key': descriptor.pageKey,
        'to route pattern': descriptor.routePattern,
        'navigation source': explicitNavigation.source,
        'navigation surface': explicitNavigation.surface,
        'navigation intent': explicitNavigation.intent,
        ...contextProperties,
      });
    }

    trackAmplitudeEvent('Page Viewed', {
      'page key': descriptor.pageKey,
      'route pattern': descriptor.routePattern,
      'previous page key': previousRoute?.descriptor.pageKey ?? null,
      'previous route pattern': previousRoute?.descriptor.routePattern ?? null,
      'navigation source': navigationSource,
      'navigation surface': navigationSurface,
      ...contextProperties,
    });

    previousRouteRef.current = {
      path,
      descriptor,
    };
  }, [isLoading, location.pathname, navigationType, path]);

  return null;
}
